'use client';

import { HTMLAttributes, forwardRef, ReactNode, useEffect, useCallback } from 'react';
import Backdrop from '../primitives/Backdrop';
import CloseButton from '../primitives/CloseButton';
import { lockBodyScroll, unlockBodyScroll } from '../utils/scrollLock';
import styles from './SideSheet.module.css';

type SideSheetVariant = 'standard' | 'modal';
type SideSheetSize = 'sm' | 'lg';

const sizeWidths: Record<SideSheetSize, number> = { sm: 400, lg: 480 };

interface SideSheetProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  onClose: () => void;
  variant?: SideSheetVariant;
  size?: SideSheetSize;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number | string;
}

const SideSheet = forwardRef<HTMLDivElement, SideSheetProps>(({
  open,
  onClose,
  variant = 'standard',
  size = 'sm',
  title,
  children,
  footer,
  width,
  className = '',
  ...props
}, ref) => {

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  // Body scroll lock — ref-counted so closed overlays can't clobber it.
  useEffect(() => {
    if (!(open && variant === 'modal')) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [open, variant]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, variant, handleKeyDown]);

  if (!open) return null;

  const resolvedWidth = width ?? sizeWidths[size];
  const sheetStyle = { width: typeof resolvedWidth === 'number' ? `${resolvedWidth}px` : resolvedWidth };

  const sheetContent = (
    <div
      ref={ref}
      className={`${styles.sheet} ${className}`}
      style={sheetStyle}
      role="dialog"
      aria-modal={variant === 'modal'}
      aria-label={title}
      {...props}
    >
      <div className={styles.header}>
        {title && <h2 className={styles.title}>{title}</h2>}
        <CloseButton className={styles.closeButton} onClick={onClose} aria-label="Close side sheet" size="lg" />
      </div>
      <div className={styles.body}>{children}</div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );

  if (variant === 'modal') {
    return (
      <div className={styles.overlay}>
        <Backdrop className={styles.backdrop} onClick={onClose} />
        {sheetContent}
      </div>
    );
  }

  return sheetContent;
});

SideSheet.displayName = 'SideSheet';
export default SideSheet;
